import { CLOUD_API_URL } from '../config/serverConfig';

function getToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const { accessToken } = JSON.parse(stored);
      return accessToken || null;
    }
  } catch {}
  return null;
}

function getRefreshToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const { refreshToken } = JSON.parse(stored);
      return refreshToken || null;
    }
  } catch {}
  return null;
}

let _refreshPromise: Promise<string | null> | null = null;
let _refreshFailed = false;

// Server explicitly rejected the refresh token (not a network error)
let _refreshRejectedByServer = false;

export function resetRefreshState() {
  _refreshFailed = false;
  _refreshRejectedByServer = false;
}

async function tryRefreshToken(): Promise<string | null> {
  if (_refreshFailed) return null;
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      console.log('[TokenRefresh] Refreshing at:', new Date().toISOString());
      const refreshToken = getRefreshToken();
      if (!refreshToken) return null;
      const res = await fetch(`${CLOUD_API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.session?.access_token) {
        _refreshFailed = true;
        _refreshRejectedByServer = true;
        localStorage.removeItem('maestro_auth');
        return null;
      }
      const newToken = data.session.access_token;
      const stored = localStorage.getItem('maestro_auth');
      const current = stored ? JSON.parse(stored) : {};
      localStorage.setItem('maestro_auth', JSON.stringify({
        ...current,
        accessToken: newToken,
        refreshToken: data.session.refresh_token || current.refreshToken,
      }));
      window.dispatchEvent(new CustomEvent('maestro:token-refreshed', { detail: { accessToken: newToken } }));
      _refreshFailed = false;
      _refreshRejectedByServer = false;
      return newToken;
    } catch {
      // Network error (offline, DNS, timeout) — credentials may still be valid.
      // Preserve maestro_auth so the next cold start can retry the refresh.
      console.warn('[TokenRefresh] Network error during refresh — credentials preserved for next launch');
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

function forceSignOut(): never {
  const hadSession = !!localStorage.getItem('maestro_auth');
  if (_refreshRejectedByServer) {
    // Server explicitly said the token is invalid — wipe credentials
    localStorage.removeItem('maestro_auth');
    try { sessionStorage.setItem('maestro_refresh_failed', '1'); } catch {}
  }
  // else: network error — keep maestro_auth intact for next cold start
  if (hadSession) {
    window.dispatchEvent(new CustomEvent('maestro:session-expired'));
  }
  throw new Error('Session expired');
}

async function fetchWithAuth(
  url: string,
  init: RequestInit,
  noAuth?: boolean,
): Promise<Response> {
  const opts = { ...init, credentials: 'omit' as RequestCredentials };
  const res = await fetch(url, opts);
  if (res.status !== 401 || noAuth) return res;

  console.warn('[TokenRefresh] 401 detected — forcing refresh', url);

  if (!getToken()) return res;

  const newToken = await tryRefreshToken();
  if (!newToken) forceSignOut();
  const retryHeaders = { ...(init.headers as Record<string, string>), Authorization: `Bearer ${newToken}` };
  return fetch(url, { ...opts, headers: retryHeaders });
}

async function handleResponse(res: Response) {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json();
}

function getMaestroLocale(): string {
  try {
    return localStorage.getItem('maestro_language') || 'en';
  } catch {
    return 'en';
  }
}

function authHeaders(extra?: Record<string, string>, noAuth?: boolean): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  if (!noAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  headers['X-Maestro-Locale'] = getMaestroLocale();
  return headers;
}

export const api = {
  get: async (path: string, opts?: { headers?: Record<string, string>; noAuth?: boolean }) => {
    const headers = authHeaders(opts?.headers, opts?.noAuth);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, { headers }, opts?.noAuth);
    return handleResponse(res);
  },

  post: async (path: string, body?: unknown, opts?: { headers?: Record<string, string>; noAuth?: boolean }) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers }, opts?.noAuth);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    }, opts?.noAuth);
    return handleResponse(res);
  },

  put: async (path: string, body?: unknown, opts?: { headers?: Record<string, string> }) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers });
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse(res);
  },

  patch: async (path: string, body?: unknown, opts?: { headers?: Record<string, string> }) => {
    const headers = authHeaders({ 'Content-Type': 'application/json', ...opts?.headers });
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse(res);
  },

  delete: async (path: string, opts?: { headers?: Record<string, string> }) => {
    const headers = authHeaders(opts?.headers);
    const res = await fetchWithAuth(`${CLOUD_API_URL}${path}`, { method: 'DELETE', headers });
    return handleResponse(res);
  },

  raw: async (path: string, init?: RequestInit) => {
    const token = getToken();
    const headers: Record<string, string> = { ...((init?.headers || {}) as Record<string, string>) };
    if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
    return fetchWithAuth(`${CLOUD_API_URL}${path}`, { ...(init || {}), headers });
  },
};

/**
 * Raw fetch to Mycelium with the same 401 → refresh → retry-once behavior as api.get/post.
 * Use for endpoints not covered by api.* (e.g. tunnel poll, batched events).
 */
export async function fetchCloudAuthenticated(
  path: string,
  init?: RequestInit,
  opts?: { noAuth?: boolean },
): Promise<Response> {
  const headers = authHeaders(init?.headers as Record<string, string> | undefined, opts?.noAuth);
  return fetchWithAuth(`${CLOUD_API_URL}${path}`, { ...init, headers }, opts?.noAuth);
}

export default api;
